import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PackagesFilterCard from "../PackagesFilterCard/PackagesFilterCard";
import PackagesPieChart from "../PackagesPieChart/PackagesPieChart";
import PackageTable from "../PackageTable/PackageTable";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { Search as SearchIcon } from "@mui/icons-material";
import { useMonitoringPackages } from "../../../../../customHooks/useMonitoringPackages";
import { isGreaterEndDate } from "../../../../../utils/helpers";
import { useDispatch } from "react-redux";
import {
  setPackageDataBeenFetched,
  setPage,
  setSelectedPackage,
  setSelectedPackageDateRange,
} from "../../../../../features/monitoringPackagesSlice";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { Box, Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { PackagesEndPointLogic } from "../../../../../logic/DataCollection/Monitoring/PackagesEndPointLogic";
import { CustomDateRangePicker } from "../../../../ReusableDateRangePickerUI/CustomDateRangePicker";
import PackageSkeleton from "./PackageSkeleton";
import { normalizeFilters } from "../packageHelper";
import { styled } from "@mui/material/styles";

const tableHeadings = {
  package_name: "Package",
  process_name: "Process Name",
  start_time: "First run",
  end_time: "Last run",
  process_count: "Process Count",
  status: "Status",
  brand: "Brand",
  interface: "Interface",
  run_method: "Run Method",
  run_by: "Run By",
};

// ✅ Moved outside component — prevents recreation on every render
// ✅ Simple ul-based listbox — no react-window, no double scrollbar
const StyledListbox = styled("ul")(() => ({
  padding: "8px 0",
  margin: 0,
  overflow: "auto",
  maxHeight: "320px",
  listStyle: "none",
  display: "block",
  "& li": {
    whiteSpace: "nowrap",
    padding: "10px 16px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

const ListboxComponent = React.forwardRef(function ListboxComponent(
  props: React.HTMLAttributes<HTMLElement>,
  ref: any,
) {
  const { children, ...other } = props;
  return (
    <StyledListbox ref={ref} {...other}>
      {children}
    </StyledListbox>
  );
});

const PackagesBase = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    packageList,
    isPackagesLoading,
    hasPackageDataBeenFetched,
    selectedPackage,
    selectedProcessName,
    selectedPackageStatus,
    selectedPackageBrand,
    selectedPackageInterface,
    selectedPackageDateRange,
  } = useSelector((state: any) => state.monitoringPackages);

  // ✅ Custom 1400px breakpoint — independent of MUI theme breakpoints
  const isWideScreen = useMediaQuery("(min-width:1300px)");

  const { fetchMonitoringPackages } = useMonitoringPackages();

  const getCurrentFilters = () => {
    const paramPackageName = searchParams.get("package_name");
    return {
      package_name: paramPackageName ? [paramPackageName] : undefined,
      process_id: selectedProcessName?.length ? selectedProcessName : undefined,
      status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
      brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
      interface_id: selectedPackageInterface?.length
        ? selectedPackageInterface
        : undefined,
    };
  };

  const filters = normalizeFilters(getCurrentFilters());

  const getPackages = () => {
    const from_date =
      selectedPackageDateRange &&
      selectedPackageDateRange?.[0]?.format("YYYY-MM-DD");
    const to_date =
      selectedPackageDateRange &&
      selectedPackageDateRange?.[1]?.format("YYYY-MM-DD");

    dispatch(setPackageDataBeenFetched(false));
    fetchMonitoringPackages(from_date, to_date, 1000, 0, undefined, filters);
    dispatch(setPage(0));

    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", "0");
    updatedParams.set("start_date", from_date);
    updatedParams.set("end_date", to_date);

    if (selectedPackage) {
      updatedParams.set("package_name", selectedPackage);
    } else {
      updatedParams.delete("package_name");
    }

    setSearchParams(updatedParams);
  };

  useEffect(() => {
    PackagesEndPointLogic.getAllFilters(
      oauthTokenHeader,
      ["status", "BRAND", "process_name", "interface", "package"],
      dispatch,
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!oauthTokenHeader) return;
    const paramStart = searchParams.get("start_date");
    const paramEnd = searchParams.get("end_date");
    const paramPackageName = searchParams.get("package_name");

    if (paramPackageName !== selectedPackage) {
      dispatch(setSelectedPackage(paramPackageName || ""));
    }

    const initialFilters = normalizeFilters({
      package_name: paramPackageName ? [paramPackageName] : undefined,
      process_id: selectedProcessName?.length ? selectedProcessName : undefined,
      status: selectedPackageStatus?.length ? selectedPackageStatus : undefined,
      brand_id: selectedPackageBrand?.length ? selectedPackageBrand : undefined,
      interface_id: selectedPackageInterface?.length
        ? selectedPackageInterface
        : undefined,
    });

    const today = dayjs().format("YYYY-MM-DD");
    const from_date = paramStart ? dayjs(paramStart) : dayjs(today);
    const to_date = paramEnd ? dayjs(paramEnd) : dayjs(today);
    dispatch(setSelectedPackageDateRange([dayjs(from_date), dayjs(to_date)]));

    dispatch(setPackageDataBeenFetched(false));

    fetchMonitoringPackages(
      from_date.format("YYYY-MM-DD"),
      to_date.format("YYYY-MM-DD"),
      1000,
      0,
      undefined,
      initialFilters,
    );

    if (!paramStart || !paramEnd) {
      const urlParams: Record<string, string> = {
        start_date: from_date.format("YYYY-MM-DD"),
        end_date: to_date.format("YYYY-MM-DD"),
      };

      if (paramPackageName) {
        urlParams.package_name = paramPackageName;
      }

      setSearchParams(urlParams);
    }
    // eslint-disable-next-line
  }, [oauthTokenHeader, searchParams]);

  const packageNames = [
    ...new Set(packageList.map((pkg: any) => pkg.package_name)),
  ];

  return (
    <>
      <Paper elevation={0} sx={{ padding: "8px 16px" }}>
        <Grid container alignItems="center" rowSpacing={1}>

          {/* ✅ Title — full width below 1400px, 4 cols above */}
          <Grid size={{ xs: 12 }} sx={{ maxWidth: isWideScreen ? "33.33%" : "100%", flexBasis: isWideScreen ? "33.33%" : "100%" }}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              Data Workflows
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }}>
              Inspect process runs (data updating process) and get a macro
              overview of the workflows.
            </Typography>
          </Grid>

          {/* ✅ Controls — full width below 1400px, 8 cols above */}
          <Grid size={{ xs: 12 }} sx={{ maxWidth: isWideScreen ? "66.66%" : "100%", flexBasis: isWideScreen ? "66.66%" : "100%" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent={isWideScreen ? "flex-end" : "stretch"}
              flexWrap={{ xs: "nowrap", sm: "wrap", md: "nowrap" }}
              spacing={1.5}
              sx={{ width: "100%" }}
            >
              {/* Date Picker */}
              <Box
                sx={{
                  minWidth: { xs: "100%", sm: 260 },
                  maxWidth: { xs: "100%", sm: 250 },
                  flex: isWideScreen ? "0 1 auto" : "1 1 auto",
                }}
              >
                <CustomDateRangePicker
                  isDisabled={isPackagesLoading}
                  value={selectedPackageDateRange}
                  onChange={(newValue: any) => {
                    const [startDate, endDate] = newValue || [];
                    if (
                      startDate &&
                      endDate &&
                      isGreaterEndDate({ startDate, endDate })
                    ) {
                      dispatch(
                        setDisplayErrorSnackBar({
                          message: "Start date cannot be after end date",
                          type: "error",
                        }),
                      );
                      return;
                    }
                    dispatch(setSelectedPackageDateRange([startDate, endDate]));
                  }}
                />
              </Box>

              {/* Package Autocomplete */}
              <Autocomplete
                data-tracker="347"
                disablePortal
                disableClearable={!selectedPackage}
                size="small"
                sx={{
                  minWidth: { xs: "100%", sm: 220 },
                  flex: isWideScreen ? "0 1 auto" : "1 1 auto",
                }}
                options={packageNames || []}
                value={selectedPackage || ""}
                ListboxComponent={ListboxComponent}
                disabled={isPackagesLoading}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: "max-content",
                    },
                  },
                }}
                onChange={(_event, newValue) => {
                  dispatch(setSelectedPackage(newValue || ""));

                  if (
                    !newValue &&
                    selectedPackageDateRange &&
                    selectedPackageDateRange[0] &&
                    selectedPackageDateRange[1]
                  ) {
                    const from_date =
                      selectedPackageDateRange[0].format("YYYY-MM-DD");
                    const to_date =
                      selectedPackageDateRange[1].format("YYYY-MM-DD");

                    dispatch(setPackageDataBeenFetched(false));

                    const updatedFilters = normalizeFilters({
                      package_name: undefined,
                      process_id: selectedProcessName?.length
                        ? selectedProcessName
                        : undefined,
                      status: selectedPackageStatus?.length
                        ? selectedPackageStatus
                        : undefined,
                      brand_id: selectedPackageBrand?.length
                        ? selectedPackageBrand
                        : undefined,
                      interface_id: selectedPackageInterface?.length
                        ? selectedPackageInterface
                        : undefined,
                    });

                    fetchMonitoringPackages(
                      from_date,
                      to_date,
                      1000,
                      0,
                      undefined,
                      updatedFilters,
                    );
                    dispatch(setPage(0));

                    const updatedParams = new URLSearchParams(
                      window.location.search,
                    );
                    updatedParams.set("page", "0");
                    updatedParams.set("start_date", from_date);
                    updatedParams.set("end_date", to_date);
                    updatedParams.delete("package_name");
                    setSearchParams(updatedParams);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Package (optional)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                    color="secondary"
                  />
                )}
              />

              {/* GET DATA Button */}
              <Button
                data-tracker="348"
                variant="contained"
                color="secondary"
                disabled={
                  isPackagesLoading ||
                  !(
                    selectedPackageDateRange &&
                    selectedPackageDateRange[0] &&
                    selectedPackageDateRange[1]
                  )
                }
                onClick={getPackages}
                sx={{
                  padding: "8px 22px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                get data
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, marginTop: "10px" }}>
        {isPackagesLoading || !hasPackageDataBeenFetched ? (
          <PackageSkeleton />
        ) : (
          <>
            <PackagesFilterCard />
            <PackagesPieChart />
            <PackageTable tableHeadings={tableHeadings} />
          </>
        )}
      </Paper>
    </>
  );
};

export default PackagesBase;